import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { Button } from '@smorgrav/velpr-ui'
import type {
  ActivityStats,
  DailyActivityDoc,
  OrderKind,
  UsageRecord
} from '@smorgrav/hundehall_shared'
import {
  useActivityDaily,
  useActivitySince,
  type ActivitySince
} from '@/hooks/useActivity'

export const Route = createFileRoute('/')({
  component: Dashboard
})

const RANGES = [7, 30, 90, 365]

const KIND_LABELS: Record<OrderKind, string> = {
  slot: 'Bookinger',
  event: 'Arrangementer',
  mixed: 'Begge',
  other: 'Annet (gavekort, klippekort)'
}

function Dashboard() {
  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Aktivitet på tvers av alle anlegg. Dager er i UTC.
        </p>
      </div>
      <SinceRelease />
      <DailyActivity />
    </div>
  )
}

// --- Since release -----------------------------------------------------------

function SinceRelease() {
  // '' = latest deploy (server default)
  const [since, setSince] = useState('')
  const { data, error, loading, refetch } = useActivitySince(since)

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Siden release</h2>
          <p className="text-muted-foreground text-sm">
            Fullførte ordre etter valgt tidspunkt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="bg-background rounded-md border px-2 py-1 text-sm"
            value={since}
            onChange={(e) => setSince(e.target.value)}
          >
            <option value="">Siste release</option>
            {data?.deploys.map((d) => (
              <option key={d.release} value={d.deployedAt}>
                {d.release} ({Object.keys(d.targets).join(', ')})
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={loading}
          >
            Oppdater
          </Button>
        </div>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}
      {loading && !data && (
        <p className="text-muted-foreground text-sm">Laster...</p>
      )}
      {data && <SinceReleaseBody data={data} />}
    </section>
  )
}

function SinceReleaseBody({ data }: { data: ActivitySince }) {
  const release = data.deploys.find((d) => d.deployedAt === data.since)
  const sinceDt = DateTime.fromISO(data.since)
  const sites = Object.entries(data.sites).sort(
    ([, a], [, b]) => b.orders - a.orders
  )

  return (
    <div className="space-y-4">
      <div className="bg-muted/30 rounded-lg border p-4">
        <div className="text-muted-foreground text-sm">
          {release ? `Release ${release.release}` : 'Siden'} ·{' '}
          {sinceDt.toLocal().toFormat('dd.MM.yyyy HH:mm')} (
          {sinceDt.toRelative({ locale: 'nb' })})
          {data.deploys.length === 0 && ' · ingen deploy registrert, viser 24t'}
        </div>
        <div className="mt-1 flex items-baseline gap-3">
          <span className="text-4xl font-bold tabular-nums">
            {data.totals.orders}
            {data.truncated && '+'}
          </span>
          <span className="text-muted-foreground">
            ordre på {data.totals.activeSites} anlegg ·{' '}
            {fmtAmount(data.totals.amount)}
          </span>
        </div>
        {data.totals.lastBookedAt && (
          <div className="text-muted-foreground mt-1 text-sm">
            Siste ordre {fmtTime(data.totals.lastBookedAt)}
          </div>
        )}
        {data.truncated && (
          <div className="text-muted-foreground mt-1 text-xs">
            Mange ordre i perioden — tallene er avkortet. Bruk daglig oversikt
            for lengre perioder.
          </div>
        )}
      </div>

      {data.totals.orders > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <ProviderTable providers={data.totals.providers} />
          <SiteTable rows={sites} names={data.siteNames} showLast />
        </div>
      )}

      {data.latest.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Tid</th>
                <th className="px-3 py-2 text-left">Anlegg</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Betaling</th>
                <th className="px-3 py-2 text-right">Beløp</th>
              </tr>
            </thead>
            <tbody>
              {data.latest.map((o) => (
                <tr key={`${o.siteId}/${o.orderId}`} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fmtTime(o.bookedAt)}
                  </td>
                  <td className="px-3 py-2">
                    {data.siteNames[o.siteId] ?? o.siteId}
                  </td>
                  <td className="px-3 py-2">{KIND_LABELS[o.kind]}</td>
                  <td className="px-3 py-2">{o.provider}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtAmount(o.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- Daily activity ----------------------------------------------------------

function DailyActivity() {
  const [range, setRange] = useState(30)
  const { data, error, loading } = useActivityDaily(range)

  // Stored days plus today (live), oldest first
  const days = useMemo(() => (data ? [...data.days, data.today] : []), [data])
  const summary = useMemo(() => sumDays(days), [days])

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Daglig aktivitet</h2>
          <p className="text-muted-foreground text-sm">
            Oppdateres hver natt. I dag er beregnet nå.
          </p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`rounded-full border px-3 py-1 text-xs ${
                r === range
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'hover:bg-muted'
              }`}
            >
              {r} dager
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}
      {loading && !data && (
        <p className="text-muted-foreground text-sm">Laster...</p>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Stat
              label="Ordre"
              value={summary.orders.toLocaleString('nb-NO')}
            />
            <Stat label="Omsetning" value={fmtAmount(summary.amount)} />
            <Stat
              label="Slot-bookinger"
              value={summary.tickets.toLocaleString('nb-NO')}
            />
            <Stat
              label="Påmeldinger"
              value={summary.eventSignups.toLocaleString('nb-NO')}
            />
            <Stat
              label="Aktive anlegg"
              value={String(Object.keys(summary.sites).length)}
            />
          </div>

          <OrdersChart days={days} />

          <div className="grid gap-4 lg:grid-cols-3">
            <ProviderTable providers={summary.providers} />
            <KindTable kinds={summary.kinds} />
            <SiteTable
              rows={Object.entries(summary.sites)
                .sort(([, a], [, b]) => b.orders - a.orders)
                .slice(0, 15)}
              names={data.siteNames}
            />
          </div>
          <p className="text-muted-foreground text-xs">
            Unike brukere per dag (sum over perioden teller samme bruker flere
            ganger): {summary.userDays.toLocaleString('nb-NO')}
          </p>
        </>
      )}
    </section>
  )
}

// One single-series bar per day. Hover shows the day's numbers.
function OrdersChart({ days }: { days: DailyActivityDoc[] }) {
  const max = Math.max(1, ...days.map((d) => d.totals.orders))
  const labelEvery = Math.ceil(days.length / 8)

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-2 flex items-baseline justify-between text-sm">
        <span className="font-medium">Ordre per dag</span>
        <span className="text-muted-foreground text-xs tabular-nums">
          maks {max}
        </span>
      </div>
      <div className="flex h-40 items-end gap-[2px] border-b">
        {days.map((d, i) => {
          const isToday = i === days.length - 1
          const height = (d.totals.orders / max) * 100
          return (
            <div
              key={d.date}
              className="group relative flex h-full flex-1 items-end"
            >
              <div
                className={`w-full rounded-t-[4px] ${
                  isToday ? 'bg-primary/50' : 'bg-primary'
                } group-hover:opacity-80`}
                style={{
                  height: `${d.totals.orders ? Math.max(height, 2) : 0}%`
                }}
              />
              <div className="bg-popover text-popover-foreground pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 rounded-md border px-2 py-1 text-xs whitespace-nowrap shadow group-hover:block">
                <div className="font-medium">
                  {d.date}
                  {isToday && ' (i dag, så langt)'}
                </div>
                <div className="tabular-nums">
                  {d.totals.orders} ordre · {fmtAmount(d.totals.amount)}
                </div>
                <div className="text-muted-foreground tabular-nums">
                  {d.totals.activeSites} anlegg · {d.totals.uniqueUsers} brukere
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="text-muted-foreground mt-1 flex gap-[2px] text-[10px]">
        {days.map((d, i) => (
          <div
            key={d.date}
            className="flex-1 overflow-visible whitespace-nowrap"
          >
            {i % labelEvery === 0 ? d.date.slice(5) : ''}
          </div>
        ))}
      </div>
    </div>
  )
}

// --- Building blocks ---------------------------------------------------------

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  )
}

function ProviderTable({
  providers
}: {
  providers: Record<string, UsageRecord>
}) {
  const rows = Object.entries(providers).sort(
    ([, a], [, b]) => b.count - a.count
  )
  return (
    <SmallTable
      title="Betalingsmetode"
      headers={['Metode', 'Ordre', 'Beløp']}
      rows={rows.map(([provider, r]) => [
        provider,
        r.count.toLocaleString('nb-NO'),
        fmtAmount(r.amount)
      ])}
    />
  )
}

function KindTable({ kinds }: { kinds: Record<OrderKind, number> }) {
  return (
    <SmallTable
      title="Hva ble kjøpt"
      headers={['Type', 'Ordre']}
      rows={(Object.keys(KIND_LABELS) as OrderKind[]).map((kind) => [
        KIND_LABELS[kind],
        kinds[kind].toLocaleString('nb-NO')
      ])}
    />
  )
}

function SiteTable({
  rows,
  names,
  showLast
}: {
  rows: [string, ActivityStats & { lastBookedAt?: string }][]
  names: Record<string, string>
  showLast?: boolean
}) {
  return (
    <SmallTable
      title="Anlegg"
      headers={
        showLast
          ? ['Anlegg', 'Ordre', 'Beløp', 'Siste']
          : ['Anlegg', 'Ordre', 'Beløp']
      }
      rows={rows.map(([siteId, s]) => {
        const row = [
          names[siteId] ?? siteId,
          s.orders.toLocaleString('nb-NO'),
          fmtAmount(s.amount)
        ]
        if (showLast) row.push(s.lastBookedAt ? fmtTime(s.lastBookedAt) : '')
        return row
      })}
    />
  )
}

function SmallTable({
  title,
  headers,
  rows
}: {
  title: string
  headers: string[]
  rows: string[][]
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-muted-foreground text-xs uppercase">
          <tr>
            {headers.map((h, i) => (
              <th
                key={h}
                className={`px-3 py-2 ${i === 0 ? 'text-left' : 'text-right'}`}
              >
                {i === 0 ? title : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={headers.length}
                className="text-muted-foreground px-3 py-2"
              >
                Ingen data
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={row[0]} className="border-t">
              {row.map((cell, i) => (
                <td
                  key={i}
                  className={`px-3 py-2 ${
                    i === 0 ? '' : 'text-right tabular-nums whitespace-nowrap'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// --- Helpers -----------------------------------------------------------------

interface RangeSummary {
  orders: number
  amount: number
  tickets: number
  eventSignups: number
  userDays: number
  kinds: Record<OrderKind, number>
  providers: Record<string, UsageRecord>
  sites: Record<string, ActivityStats>
}

// Sums daily docs. Unique users can't be summed across days (the docs hold
// counts, not ids), hence userDays.
const sumDays = (days: DailyActivityDoc[]): RangeSummary => {
  const sum: RangeSummary = {
    orders: 0,
    amount: 0,
    tickets: 0,
    eventSignups: 0,
    userDays: 0,
    kinds: { slot: 0, event: 0, mixed: 0, other: 0 },
    providers: {},
    sites: {}
  }
  const addRecord = (
    target: Record<string, UsageRecord>,
    key: string,
    r: UsageRecord
  ) => {
    const cur = target[key] ?? { count: 0, amount: 0 }
    target[key] = { count: cur.count + r.count, amount: cur.amount + r.amount }
  }
  for (const day of days) {
    const t = day.totals
    sum.orders += t.orders
    sum.amount += t.amount
    sum.tickets += t.tickets
    sum.eventSignups += t.eventSignups
    sum.userDays += t.uniqueUsers
    for (const kind of Object.keys(sum.kinds) as OrderKind[]) {
      sum.kinds[kind] += t.kinds[kind] ?? 0
    }
    for (const [p, r] of Object.entries(t.providers)) {
      addRecord(sum.providers, p, r)
    }
    for (const [siteId, s] of Object.entries(day.sites)) {
      const cur = sum.sites[siteId]
      sum.sites[siteId] = {
        ...(cur ?? s),
        orders: (cur?.orders ?? 0) + s.orders,
        amount: (cur?.amount ?? 0) + s.amount
      }
    }
  }
  return sum
}

const fmtAmount = (n: number | undefined) =>
  `${(n ?? 0).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`

const fmtTime = (iso: string) =>
  DateTime.fromISO(iso).toLocal().toFormat('dd.MM HH:mm')
